import { useEffect, useRef, useState, type ReactNode } from "react";
import { MoreHorizontal } from "lucide-react";
import clsx from "clsx";
import { initials, personRole, roleVar, type Role } from "@/lib/colors";

/** Round initials avatar with a stable per-person colour. */
export function Avatar({
  name,
  size = 24,
  role,
  muted = false,
  className,
}: {
  name: string | null | undefined;
  size?: number;
  role?: Role;
  muted?: boolean;
  className?: string;
}) {
  return (
    <span
      className={clsx(
        "avatar",
        muted && "!bg-ink-800 !text-mut-300 ring-1 ring-fg/[0.1]",
        className,
      )}
      style={{
        ...roleVar(role ?? personRole(name)),
        width: size,
        height: size,
        fontSize: size * 0.4,
      }}
      title={name ?? undefined}
      aria-hidden
    >
      {initials(name)}
    </span>
  );
}

/** Rounded square monogram for a company/deal, tinted by a colour role. */
export function Monogram({ name, role, size = 32 }: { name: string; role: Role; size?: number }) {
  return (
    <span
      className="inline-grid place-items-center rounded-[10px] font-semibold shrink-0"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.38,
        color: `rgb(var(--${role}))`,
        background: `rgb(var(--${role}) / 0.12)`,
      }}
      aria-hidden
    >
      {initials(name)}
    </span>
  );
}

/** Pill-style segmented control. */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="inline-flex gap-0.5 rounded-[10px] bg-ink-800 p-[3px]"
    >
      {options.map((o) => (
        <button
          key={o.value}
          role="radio"
          aria-checked={value === o.value}
          onClick={() => onChange(o.value)}
          className={clsx(
            "px-3 py-1 rounded-[8px] text-sm font-medium transition-colors",
            value === o.value
              ? "bg-ink-900 text-mut-100 shadow-ring"
              : "text-mut-300 hover:text-mut-100",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** "⋯" overflow menu for rarely-used actions. */
export function OverflowMenu({
  items,
  label = "More actions",
}: {
  items: { label: string; icon?: ReactNode; onClick: () => void; danger?: boolean }[];
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <button
        className="btn btn-ghost !px-2"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <MoreHorizontal className="w-4 h-4" />
      </button>
      {open && (
        <div
          role="menu"
          className="panel absolute right-0 mt-1.5 z-50 min-w-[200px] p-1 animate-fade-up"
        >
          {items.map((it) => (
            <button
              key={it.label}
              role="menuitem"
              className={clsx(
                "w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-md text-left hover:bg-fg/[0.05]",
                it.danger && "text-accent-pink",
              )}
              onClick={() => {
                setOpen(false);
                it.onClick();
              }}
            >
              {it.icon}
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Small empty-state block. */
export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="py-10 text-center">
      <div className="text-md font-medium text-mut-200">{title}</div>
      {children && <div className="mt-1 text-sm text-mut-500">{children}</div>}
    </div>
  );
}
